import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type { SectionType } from '../../types';
import { SectionCard } from '../../components/shared/FormSectionWidgets';

interface PreviewSection {
  question: string;
  sectionType: SectionType;
  machinePartName?: string;
  minValue?: number;
  maxValue?: number;
  warningMin?: number;
  warningMax?: number;
}

interface TemplatePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  templateName: string;
  machineName?: string;
  sections: PreviewSection[];
}

const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({
  open,
  onClose,
  templateName,
  machineName,
  sections,
}) => {
  const generalSections = sections.filter(s => !s.machinePartName);
  const partGroups: Record<string, PreviewSection[]> = {};
  sections.forEach(s => {
    if (s.machinePartName) {
      if (!partGroups[s.machinePartName]) partGroups[s.machinePartName] = [];
      partGroups[s.machinePartName].push(s);
    }
  });

  const renderSections = (items: PreviewSection[], keyPrefix: string) =>
    items.map((s, i) => (
      <SectionCard
        key={`${keyPrefix}-${i}`}
        sectionId={`${keyPrefix}-${i}`}
        question={s.question}
        sectionType={s.sectionType}
        index={i}
        thresholds={
          s.minValue != null || s.maxValue != null || s.warningMin != null || s.warningMax != null
            ? {
                criticalMin: s.minValue,
                criticalMax: s.maxValue,
                warningMin: s.warningMin,
                warningMax: s.warningMax,
              }
            : undefined
        }
        disabled
      />
    ));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight={600}>{templateName}</Typography>
            {machineName && (
              <Typography variant="caption" color="text.secondary">
                Machine: {machineName}
              </Typography>
            )}
          </Box>
          <Chip label="Preview" size="small" color="primary" variant="outlined" />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        {sections.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No sections to preview. Add some questions first.
          </Typography>
        )}

        {generalSections.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              General
            </Typography>
            {renderSections(generalSections, 'general')}
          </Box>
        )}

        {Object.entries(partGroups).map(([partName, items], groupIdx) => (
          <Box key={partName} sx={{ mb: 2 }}>
            {groupIdx > 0 || generalSections.length > 0 ? <Divider sx={{ mb: 2 }} /> : null}
            <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1 }}>
              {partName}
            </Typography>
            {renderSections(items, `part-${groupIdx}`)}
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatePreviewDialog;
